import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Chip, IconButton, Button,
  TextField, InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, CircularProgress, Alert, Snackbar,
  Tooltip, Divider, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Autocomplete, Tabs, Tab, Menu, ListItemIcon, ListItemText,
} from '@mui/material';
import {
  Add as AddIcon, Search as SearchIcon, Edit as EditIcon, Delete as DeleteIcon,
  Close as CloseIcon, Refresh as RefreshIcon, Print as PrintIcon,
  Share as ShareIcon,
  Description as DocIcon, Person as PersonIcon, ContentCopy as DuplicateIcon,
  MoreVert as MoreIcon, Visibility as ViewIcon, MedicalServices as MedicalIcon,
  AssignmentTurnedIn as CertIcon, AutoAwesome as AiIcon,
  LocalPharmacy as PharmacyIcon,
} from '@mui/icons-material';
import axios from '../../config/axios';
import { getApiUrl } from '../../config/environment';
import { buildCertificatePrintHtml } from '../../utils/certificatePrintDocument';
import { useClinicPageBreakpoints, clinicPageContainerSx } from '../../hooks/useClinicPageBreakpoints';

import SendDocumentDialog from '../../components/SendDocumentDialog';

const typeLabels: Record<string, string> = {
  medical_leave: 'Atestado Médico', attendance: 'Declaração de Comparecimento',
  fit_to_work: 'Aptidão ao Trabalho', accompaniment: 'Acompanhante',
  custom: 'Personalizado',
};
const typeColors: Record<string, string> = {
  medical_leave: '#ef4444', attendance: '#3b82f6', fit_to_work: '#22c55e',
  accompaniment: '#f59e0b', custom: '#8b5cf6',
};

const Certificates: React.FC = () => {
  const { isMobile } = useClinicPageBreakpoints();

  const [certificates, setCertificates] = useState<any[]>([]);
  const [stats, setStats] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as any });
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuItem, setMenuItem] = useState<any>(null);
  const [templates, setTemplates] = useState<any[]>([]);
  const printRef = useRef<HTMLDivElement>(null);

  // AI certificate states
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiPatientName, setAiPatientName] = useState('');
  const [aiReason, setAiReason] = useState('');
  const [aiError, setAiError] = useState('');

  // Form
  const [form, setForm] = useState<any>({
    certificate_type: 'medical_leave', title: '', content: '', patient_id: '',
    professional_id: '', issue_date: new Date().toISOString().split('T')[0],
    days_off: '', start_date: '', end_date: '', cid_code: '', cid_description: '', notes: '',
  });
  const [patientSearch, setPatientSearch] = useState('');
  const [patientOptions, setPatientOptions] = useState<any[]>([]);
  const [profSearch, setProfSearch] = useState('');
  const [profOptions, setProfOptions] = useState<any[]>([]);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [sendDocData, setSendDocData] = useState<any>(null);
  const lastPatientSearchRef = useRef<string>('');
  const lastProfSearchRef = useRef<string>('');

  useEffect(() => { loadData(); }, []);
  useEffect(() => { loadCertificates(); }, [search, filterType]);

  // Carregar pacientes e profissionais quando o dialog abrir
  useEffect(() => {
    if (openDialog) {
      // Carregar lista inicial quando o dialog abre
      searchPatients('');
      searchProfessionals('');
    }
  }, [openDialog]);

  // Buscar pacientes com debounce (evitar loop infinito)
  useEffect(() => {
    // Só buscar se o valor realmente mudou
    if (patientSearch === lastPatientSearchRef.current) {
      return;
    }
    
    const timer = setTimeout(() => {
      if (openDialog && patientSearch !== undefined) {
        lastPatientSearchRef.current = patientSearch;
        searchPatients(patientSearch);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [patientSearch, openDialog]);

  // Buscar profissionais com debounce (evitar loop infinito)
  useEffect(() => {
    // Só buscar se o valor realmente mudou
    if (profSearch === lastProfSearchRef.current) {
      return;
    }
    
    const timer = setTimeout(() => {
      if (openDialog && profSearch !== undefined) {
        lastProfSearchRef.current = profSearch;
        searchProfessionals(profSearch);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [profSearch, openDialog]);

  const loadData = async () => {
    await Promise.all([loadCertificates(), loadStats(), loadTemplates()]);
  };

  const loadCertificates = async () => {
    try {
      setLoading(true);
      const params: any = { limit: 50 };
      if (search) params.search = search;
      if (filterType) params.certificate_type = filterType;
      const { data } = await axios.get(getApiUrl('/clinic/certificates'), { params });
      setCertificates(data.data || []);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const loadStats = async () => {
    try {
      const { data } = await axios.get(getApiUrl('/clinic/certificates/stats'));
      setStats(data.data || {});
    } catch (e) { console.error(e); }
  };

  const loadTemplates = async () => {
    try {
      const { data } = await axios.get(getApiUrl('/clinic/certificates/templates'));
      setTemplates(data.data || []);
    } catch (e) { console.error(e); }
  };

  const searchPatients = async (q: string) => {
    try {
      const { data } = await axios.get(getApiUrl('/clinic/budgets-search/patients'), { params: { search: q || '' } });
      if (data?.success !== false) {
        setPatientOptions(data?.data || []);
      } else {
        setPatientOptions([]);
      }
    } catch (e) { 
      console.error('Error searching patients:', e);
      setPatientOptions([]);
    }
  };

  const searchProfessionals = async (q: string) => {
    try {
      const { data } = await axios.get(getApiUrl('/clinic/budgets-search/professionals'), { params: { search: q || '' } });
      if (data?.success !== false) {
        setProfOptions(data?.data || []);
      } else {
        setProfOptions([]);
      }
    } catch (e) { 
      console.error('Error searching professionals:', e);
      setProfOptions([]);
    }
  };

  // Função para limpar e mapear dados do formulário para o formato esperado pelo backend
  const cleanFormData = (data: any) => {
    // Campos obrigatórios que não podem ser null
    const requiredFields = ['title', 'content', 'certificate_type'];
    
    const cleaned: any = {
      certificate_type: data.certificate_type || 'medical_leave',
      title: data.title || typeLabels[data.certificate_type] || 'Atestado', // Usar label do tipo se title vazio
      content: data.content || '', // Conteúdo é obrigatório, mas pode ser string vazia
      patient_id: data.patient_id || null,
      professional_id: data.professional_id || null,
      issue_date: data.issue_date || new Date().toISOString().split('T')[0],
      notes: data.notes || null,
      internal_notes: data.internal_notes || null,
    };

    // Mapear campos de CID
    if (data.cid_code) {
      cleaned.diagnosis_code = data.cid_code;
    }
    if (data.cid_description) {
      cleaned.diagnosis = data.cid_description;
    }

    // Mapear campos de afastamento
    if (data.days_off) {
      cleaned.leave_days = Number(data.days_off) || 0;
    }
    if (data.start_date) {
      cleaned.leave_start_date = data.start_date;
    }
    if (data.end_date) {
      cleaned.leave_end_date = data.end_date;
    }

    // Remover campos vazios, mas preservar campos obrigatórios
    Object.keys(cleaned).forEach(key => {
      // Não converter campos obrigatórios para null
      if (requiredFields.includes(key)) {
        // Garantir que campos obrigatórios tenham pelo menos string vazia
        if (cleaned[key] === null || cleaned[key] === undefined) {
          if (key === 'title') {
            cleaned[key] = typeLabels[cleaned.certificate_type] || 'Atestado';
          } else {
            cleaned[key] = '';
          }
        }
      } else {
        // Campos opcionais podem ser null
        if (cleaned[key] === '' || cleaned[key] === undefined) {
          cleaned[key] = null;
        }
      }
    });

    return cleaned;
  };

  const handleSave = async () => {
    try {
      const cleanedData = cleanFormData(form);
      
      if (editing) {
        await axios.put(getApiUrl(`/clinic/certificates/${editing.id}`), cleanedData);
        setSnackbar({ open: true, message: 'Atestado atualizado!', severity: 'success' });
      } else {
        await axios.post(getApiUrl('/clinic/certificates'), cleanedData);
        setSnackbar({ open: true, message: 'Atestado criado!', severity: 'success' });
      }
      setOpenDialog(false);
      resetForm();
      loadData();
    } catch (e: any) {
      setSnackbar({ open: true, message: e.response?.data?.error || 'Erro ao salvar', severity: 'error' });
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Deseja excluir este atestado?')) return;
    try {
      await axios.delete(getApiUrl(`/clinic/certificates/${id}`));
      setSnackbar({ open: true, message: 'Excluído!', severity: 'success' });
      loadData();
    } catch (e) { setSnackbar({ open: true, message: 'Erro ao excluir', severity: 'error' }); }
  };

  const handleDuplicate = async (id: string) => {
    try {
      await axios.post(getApiUrl(`/clinic/certificates/${id}/duplicate`));
      setSnackbar({ open: true, message: 'Duplicado!', severity: 'success' });
      loadData();
    } catch (e) { setSnackbar({ open: true, message: 'Erro ao duplicar', severity: 'error' }); }
  };

  const handlePrint = async (id: string) => {
    try {
      const { data } = await axios.get(getApiUrl(`/clinic/certificates/${id}/print`));
      const printData = data.data;
      const w = window.open('', '_blank');
      if (!w) return;
      w.document.write(buildCertificatePrintHtml(printData));
      w.document.close();
      setTimeout(() => w.print(), 500);
    } catch (e) { setSnackbar({ open: true, message: 'Erro ao imprimir', severity: 'error' }); }
  };

  const handleEdit = (cert: any) => {
    setEditing(cert);
    setForm({
      certificate_type: cert.certificate_type || 'medical_leave',
      title: cert.title || '', content: cert.content || '',
      patient_id: cert.patient_id || '', professional_id: cert.professional_id || '',
      issue_date: cert.issue_date || new Date().toISOString().split('T')[0],
      days_off: cert.leave_days || '', // Mapear leave_days para days_off
      start_date: cert.leave_start_date || '', // Mapear leave_start_date para start_date
      end_date: cert.leave_end_date || '', // Mapear leave_end_date para end_date
      cid_code: cert.diagnosis_code || '', // Mapear diagnosis_code para cid_code
      cid_description: cert.diagnosis || '', // Mapear diagnosis para cid_description
      notes: cert.notes || '',
    });
    if (cert.patient_snapshot) setPatientOptions([{ id: cert.patient_id, full_name: cert.patient_snapshot.full_name }]);
    if (cert.professional_snapshot) setProfOptions([{ id: cert.professional_id, full_name: cert.professional_snapshot.full_name }]);
    // Limpar refs para evitar loop
    lastPatientSearchRef.current = '';
    lastProfSearchRef.current = '';
    setOpenDialog(true);
  };

  const handleNew = () => {
    setEditing(null);
    resetForm();
    setOpenDialog(true);
  };

  const resetForm = () => {
    setForm({
      certificate_type: 'medical_leave', title: '', content: '', patient_id: '',
      professional_id: '', issue_date: new Date().toISOString().split('T')[0],
      days_off: '', start_date: '', end_date: '', cid_code: '', cid_description: '', notes: '',
    });
    setPatientOptions([]);
    setProfOptions([]);
    setPatientSearch('');
    setProfSearch('');
    // Resetar refs para evitar problemas na próxima abertura
    lastPatientSearchRef.current = '';
    lastProfSearchRef.current = '';
  };

  const applyTemplate = (template: any) => {
    setForm((f: any) => ({
      ...f,
      certificate_type: template.certificate_type || f.certificate_type,
      title: template.title || f.title,
      content: template.content || f.content,
    }));
  };

  const handleAiGenerate = async () => {
    if (!aiPatientName.trim() || !aiReason.trim()) {
      setAiError('Preencha o nome do paciente e o motivo/diagnóstico');
      return;
    }
    setAiGenerating(true);
    setAiError('');
    try {
      const res = await axios.post(getApiUrl('/clinic/certificates/ai-generate'), {
        patient_name: aiPatientName.trim(),
        reason: aiReason.trim(),
      });

      const aiData = res.data?.data;
      if (!aiData) throw new Error('Resposta vazia da IA');

      const rawText = typeof aiData === 'string' ? aiData : (aiData.output || aiData.text || aiData.response || aiData.certificate || JSON.stringify(aiData));

      const parsed = parseAiCertificate(rawText);

      resetForm();
      setForm((f: any) => ({
        ...f,
        certificate_type: parsed.certificate_type || 'medical_leave',
        title: parsed.title || typeLabels[parsed.certificate_type || 'medical_leave'] || 'Atestado Médico',
        content: parsed.content || '',
        cid_code: parsed.cid_code || '',
        cid_description: parsed.cid_description || '',
        days_off: parsed.days_off || '',
        notes: parsed.notes || '',
      }));

      setAiDialogOpen(false);
      setOpenDialog(true);
      setSnackbar({ open: true, message: 'Atestado gerado com IA! Revise e ajuste antes de salvar.', severity: 'success' });
    } catch (err: any) {
      setAiError(err.response?.data?.error || err.message || 'Erro ao gerar atestado com IA');
    } finally {
      setAiGenerating(false);
    }
  };

  const parseAiCertificate = (text: string) => {
    const result: any = { certificate_type: 'medical_leave', title: '', content: '', cid_code: '', cid_description: '', days_off: '', notes: '' };
    const lines = text.split('\n').map((l: string) => l.trim()).filter((l: string) => l);
    const contentLines: string[] = [];
    let inContent = false;

    for (const line of lines) {
      const lower = line.toLowerCase();

      if (lower.startsWith('tipo:') || lower.startsWith('tipo de atestado:')) {
        const val = line.split(':').slice(1).join(':').trim().toLowerCase();
        if (val.includes('comparecimento') || val.includes('attendance')) result.certificate_type = 'attendance';
        else if (val.includes('aptidão') || val.includes('aptidao') || val.includes('fit')) result.certificate_type = 'fit_to_work';
        else if (val.includes('acompanhante') || val.includes('accomp')) result.certificate_type = 'accompaniment';
        else result.certificate_type = 'medical_leave';
        continue;
      }
      if (lower.startsWith('título:') || lower.startsWith('titulo:')) {
        result.title = line.split(':').slice(1).join(':').trim();
        continue;
      }
      if (lower.startsWith('cid:') || lower.startsWith('código cid:') || lower.startsWith('codigo cid:')) {
        const cidParts = line.split(':').slice(1).join(':').trim();
        const cidMatch = cidParts.match(/^([A-Z]\d{2}(?:\.\d+)?)\s*[-–]\s*(.*)/i);
        if (cidMatch) { result.cid_code = cidMatch[1].toUpperCase(); result.cid_description = cidMatch[2].trim(); }
        else result.cid_code = cidParts;
        continue;
      }
      if (lower.startsWith('descrição cid:') || lower.startsWith('descricao cid:')) {
        result.cid_description = line.split(':').slice(1).join(':').trim();
        continue;
      }
      if (lower.startsWith('dias de afastamento:') || lower.startsWith('afastamento:') || lower.startsWith('dias:')) {
        const num = line.split(':').slice(1).join(':').trim().replace(/\D/g, '');
        if (num) result.days_off = num;
        continue;
      }
      if (lower.startsWith('observaç') || lower.startsWith('observac') || lower.startsWith('nota:') || lower.startsWith('notas:')) {
        result.notes = line.split(':').slice(1).join(':').trim();
        continue;
      }
      if (lower.startsWith('conteúdo:') || lower.startsWith('conteudo:') || lower.startsWith('texto:')) {
        inContent = true;
        const afterColon = line.split(':').slice(1).join(':').trim();
        if (afterColon) contentLines.push(afterColon);
        continue;
      }

      if (inContent || (!lower.includes(':') && line.length > 20)) {
        inContent = true;
        contentLines.push(line);
      }
    }

    if (contentLines.length > 0) result.content = contentLines.join('\n');

    return result;
  };

  return (
    <Box sx={clinicPageContainerSx}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f172a' }}>Atestados e Declarações</Typography>
          <Typography variant="body2" sx={{ color: '#64748b' }}>Emita atestados médicos e declarações diversas</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton onClick={loadData} sx={{ color: '#14B8A6' }}><RefreshIcon /></IconButton>
          <Button variant="outlined" startIcon={<AiIcon />}
            onClick={() => { setAiPatientName(''); setAiReason(''); setAiError(''); setAiDialogOpen(true); }}
            sx={{ borderColor: '#8b5cf6', color: '#8b5cf6', '&:hover': { borderColor: '#7c3aed', bgcolor: 'rgba(139,92,246,0.04)' }, textTransform: 'none', fontWeight: 600 }}>
            Criar com IA
          </Button>
          <Button variant="contained" startIcon={<AddIcon />}
            sx={{ bgcolor: '#14B8A6', '&:hover': { bgcolor: '#0d9488' }, textTransform: 'none', fontWeight: 600 }} onClick={handleNew}>
            Novo Atestado
          </Button>
        </Box>
      </Box>

      {/* KPIs */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total', value: stats.total || 0, color: '#14B8A6' },
          { label: 'Hoje', value: stats.total_today || 0, color: '#3b82f6' },
          { label: 'Este Mês', value: stats.total_month || 0, color: '#8b5cf6' },
          { label: 'Atestados', value: stats.total_medical_leave || 0, color: '#ef4444' },
          { label: 'Declarações', value: stats.total_attendance || 0, color: '#f59e0b' },
        ].map((kpi, i) => (
          <Grid item xs={6} sm={4} md={2.4} key={i}>
            <Card sx={{ bgcolor: 'white', borderRadius: 3, border: `1px solid ${kpi.color}22` }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Typography variant="caption" sx={{ color: '#64748b' }}>{kpi.label}</Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: kpi.color }}>{kpi.value}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3, borderRadius: 3 }}>
        <CardContent sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', p: 2, '&:last-child': { pb: 2 } }}>
          <TextField size="small" placeholder="Buscar..." value={search} onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
            sx={{ minWidth: 250 }} />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Tipo</InputLabel>
            <Select value={filterType} label="Tipo" onChange={(e) => setFilterType(e.target.value)}>
              <MenuItem value="">Todos</MenuItem>
              {Object.entries(typeLabels).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
            </Select>
          </FormControl>
        </CardContent>
      </Card>

      {/* Table */}
      <Card sx={{ borderRadius: 3 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                <TableCell sx={{ fontWeight: 600 }}>Tipo</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Título</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Paciente</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Profissional</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Data</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4 }}><CircularProgress size={32} /></TableCell></TableRow>
              ) : certificates.length === 0 ? (
                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: '#94a3b8' }}>Nenhum atestado encontrado</TableCell></TableRow>
              ) : certificates.map((cert) => (
                <TableRow key={cert.id} hover>
                  <TableCell>
                    <Chip label={typeLabels[cert.certificate_type] || cert.certificate_type} size="small"
                      sx={{ bgcolor: (typeColors[cert.certificate_type] || '#666') + '15', color: typeColors[cert.certificate_type] || '#666', fontWeight: 600, fontSize: '0.75rem' }} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>{cert.title || '-'}</TableCell>
                  <TableCell>{cert.patient_name || '-'}</TableCell>
                  <TableCell>{cert.professional_name || '-'}</TableCell>
                  <TableCell>{cert.issue_date ? new Date(cert.issue_date + 'T12:00:00').toLocaleDateString('pt-BR') : '-'}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handlePrint(cert.id)} sx={{ color: '#14B8A6' }}><PrintIcon fontSize="small" /></IconButton>
                    <Tooltip title="Enviar"><IconButton size="small" onClick={() => { const p = cert.patient_snapshot || {}; setSendDocData({ id: cert.id, patientId: cert.patient_id, email: p.email, phone: p.phone, whatsapp: p.whatsapp, name: p.full_name }); setSendDialogOpen(true); }} sx={{ color: '#3b82f6' }}><ShareIcon fontSize="small" /></IconButton></Tooltip>
                    <IconButton size="small" onClick={() => handleEdit(cert)} sx={{ color: '#3b82f6' }}><EditIcon fontSize="small" /></IconButton>
                    <IconButton size="small" onClick={(e) => { setMenuAnchor(e.currentTarget); setMenuItem(cert); }}><MoreIcon fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Context Menu */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        <MenuItem onClick={() => { handleDuplicate(menuItem?.id); setMenuAnchor(null); }}>
          <ListItemIcon><DuplicateIcon fontSize="small" /></ListItemIcon><ListItemText>Duplicar</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { handleDelete(menuItem?.id); setMenuAnchor(null); }} sx={{ color: '#ef4444' }}>
          <ListItemIcon><DeleteIcon fontSize="small" sx={{ color: '#ef4444' }} /></ListItemIcon><ListItemText>Excluir</ListItemText>
        </MenuItem>
      </Menu>

      {/* Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>{editing ? 'Editar Atestado' : 'Novo Atestado'}</Typography>
          <IconButton onClick={() => setOpenDialog(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {/* Templates */}
          {!editing && templates.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: '#64748b' }}>Modelos Rápidos</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {templates.map((t: any) => (
                  <Chip key={t.id} label={t.title} clickable onClick={() => applyTemplate(t)}
                    sx={{ bgcolor: '#14B8A615', color: '#14B8A6', fontWeight: 500 }} />
                ))}
              </Box>
              <Divider sx={{ my: 2 }} />
            </Box>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Tipo</InputLabel>
                <Select value={form.certificate_type} label="Tipo" onChange={(e) => setForm({ ...form, certificate_type: e.target.value })}>
                  {Object.entries(typeLabels).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Título" value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete 
                size="small" 
                options={patientOptions} 
                getOptionLabel={(o: any) => `${o.full_name || ''}${o.cpf ? ` - CPF: ${o.cpf}` : ''}${o.phone ? ` - Tel: ${o.phone}` : ''}`}
                onInputChange={(_, v, reason) => {
                  // Só atualizar se não for por seleção de opção
                  if (reason !== 'reset') {
                    setPatientSearch(v);
                  }
                }}
                onChange={(_, v) => {
                  setForm({ ...form, patient_id: v?.id || '' });
                  // Limpar busca quando selecionar para evitar loop
                  if (v) {
                    lastPatientSearchRef.current = '';
                    setPatientSearch('');
                  }
                }}
                onOpen={() => {
                  if (patientOptions.length === 0) {
                    lastPatientSearchRef.current = '';
                    setPatientSearch('');
                  }
                }}
                value={patientOptions.find((p: any) => p.id === form.patient_id) || null}
                renderInput={(params) => <TextField {...params} label="Paciente" placeholder="Buscar por nome, CPF ou telefone..." />}
                isOptionEqualToValue={(o: any, v: any) => o?.id === v?.id}
                filterOptions={(x) => x}
                loading={false}
                noOptionsText="Nenhum paciente encontrado"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete 
                size="small" 
                options={profOptions} 
                getOptionLabel={(o: any) => `${o.full_name || ''} - ${o.registration_type || 'CRM'} ${o.registration_number || ''} ${o.registration_state || ''} ${o.specialty ? `(${o.specialty})` : ''}`}
                onInputChange={(_, v, reason) => {
                  // Só atualizar se não for por seleção de opção
                  if (reason !== 'reset') {
                    setProfSearch(v);
                  }
                }}
                onChange={(_, v) => {
                  setForm({ ...form, professional_id: v?.id || '' });
                  // Limpar busca quando selecionar para evitar loop
                  if (v) {
                    lastProfSearchRef.current = '';
                    setProfSearch('');
                  }
                }}
                onOpen={() => {
                  if (profOptions.length === 0) {
                    lastProfSearchRef.current = '';
                    setProfSearch('');
                  }
                }}
                value={profOptions.find((p: any) => p.id === form.professional_id) || null}
                renderInput={(params) => <TextField {...params} label="Profissional" placeholder="Buscar por nome ou registro..." />}
                isOptionEqualToValue={(o: any, v: any) => o?.id === v?.id}
                filterOptions={(x) => x}
                loading={false}
                noOptionsText="Nenhum profissional encontrado"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" type="date" label="Data de Emissão" value={form.issue_date}
                onChange={(e) => setForm({ ...form, issue_date: e.target.value })} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" type="number" label="Dias de Afastamento" value={form.days_off}
                onChange={(e) => setForm({ ...form, days_off: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" label="CID" value={form.cid_code}
                onChange={(e) => setForm({ ...form, cid_code: e.target.value })} placeholder="Ex: J11" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" type="date" label="Data Início" value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" type="date" label="Data Fim" value={form.end_date}
                onChange={(e) => setForm({ ...form, end_date: e.target.value })} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Descrição do CID" value={form.cid_description}
                onChange={(e) => setForm({ ...form, cid_description: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={6} label="Conteúdo do Atestado" value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })}
                placeholder="Atesto para os devidos fins que o(a) paciente..." />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" multiline rows={2} label="Observações" value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave}
            sx={{ bgcolor: '#14B8A6', '&:hover': { bgcolor: '#0d9488' } }}>
            {editing ? 'Atualizar' : 'Emitir Atestado'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* AI Certificate Dialog */}
      <Dialog open={aiDialogOpen} onClose={() => !aiGenerating && setAiDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e5e7eb', pb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AiIcon sx={{ color: '#8b5cf6' }} />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Atestado com IA</Typography>
          </Box>
          <IconButton onClick={() => !aiGenerating && setAiDialogOpen(false)} disabled={aiGenerating}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ mb: 3, mt: 1 }}>
            <Alert severity="info" sx={{ borderRadius: 2, mb: 2 }}>
              A IA irá gerar o texto completo do atestado com base no motivo informado. Revise sempre antes de emitir.
            </Alert>
            {aiError && <Alert severity="error" sx={{ borderRadius: 2, mb: 2 }}>{aiError}</Alert>}
          </Box>
          <TextField
            fullWidth
            label="Nome do Paciente *"
            placeholder="Ex: Maria Silva"
            value={aiPatientName}
            onChange={(e) => setAiPatientName(e.target.value)}
            disabled={aiGenerating}
            sx={{ mb: 2.5 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><PersonIcon sx={{ color: '#9ca3af' }} /></InputAdornment> }}
          />
          <TextField
            fullWidth
            label="Motivo / Diagnóstico *"
            placeholder="Ex: Gripe com febre alta, necessita repouso por 3 dias"
            value={aiReason}
            onChange={(e) => setAiReason(e.target.value)}
            disabled={aiGenerating}
            multiline
            rows={3}
            onKeyPress={(e) => { if (e.key === 'Enter' && !e.shiftKey && !aiGenerating) { e.preventDefault(); handleAiGenerate(); } }}
            InputProps={{ startAdornment: <InputAdornment position="start"><MedicalIcon sx={{ color: '#9ca3af' }} /></InputAdornment> }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #e5e7eb', gap: 1 }}>
          <Button onClick={() => setAiDialogOpen(false)} disabled={aiGenerating} sx={{ color: '#6b7280', textTransform: 'none' }}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleAiGenerate}
            disabled={aiGenerating || !aiPatientName.trim() || !aiReason.trim()}
            startIcon={aiGenerating ? <CircularProgress size={18} color="inherit" /> : <AiIcon />}
            sx={{ bgcolor: '#8b5cf6', '&:hover': { bgcolor: '#7c3aed' }, textTransform: 'none', fontWeight: 600, minWidth: 180 }}
          >
            {aiGenerating ? 'Gerando atestado...' : 'Gerar com IA'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>

      <SendDocumentDialog
        open={sendDialogOpen}
        onClose={() => setSendDialogOpen(false)}
        documentType="certificate"
        documentId={sendDocData?.id || ''}
        patientId={sendDocData?.patientId}
        patientEmail={sendDocData?.email}
        patientPhone={sendDocData?.phone}
        patientWhatsapp={sendDocData?.whatsapp}
        patientName={sendDocData?.name}
        documentLabel="Atestado"
      />
    </Box>
  );
};

export default Certificates;
